import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { DocumentNode } from "@apollo/client";

import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import { ApiKey } from "types/generated";
import { stringIsRequired } from "utils/formValidators";

import { EditApiKeyFields } from "./types";
import { useUpdateApiKey } from "../hooks/useUpdateApiKey";

type ApiKeysEditDrawerProps = {
  apiKey: Pick<ApiKey, "id" | "description" | "name" | "teams">;
  refetchQueriesOnUpdate?: DocumentNode[];
};

const ApiKeysEditDrawer = createDrawer(
  ({ apiKey, refetchQueriesOnUpdate }: ApiKeysEditDrawerProps) => {
    const drawer = useModal();

    const { onUpdate, loading: isSubmitting } = useUpdateApiKey({
      apiKeyId: apiKey.id,
      callback: () => drawer.hide(),
      refetchQueries: refetchQueriesOnUpdate,
    });

    const editForm = useForm<EditApiKeyFields>({
      defaultValues: {
        description: apiKey.description || "",
        name: apiKey.name || "",
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = editForm;

    const handleCloseDrawer = () => {
      drawer.hide();
    };

    const handleSubmitClick = () => {
      handleSubmit(onSubmit)();
    };

    const onSubmit: SubmitHandler<EditApiKeyFields> = (formFields) => {
      const input = {
        name: formFields.name.trim(),
        description: formFields.description.trim(),
        teams: apiKey.teams,
      };
      onUpdate(input);
    };

    return (
      <DrawerForm isDirty={isDirty} dataTestId="api-key-edit-drawer">
        <FormProvider {...editForm}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Edit details" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody fullHeight hasStickyFooter>
            <FormField label="Name" error={errors?.name?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter API key name"
                  error={!!errors?.name}
                  {...register("name", {
                    validate: stringIsRequired("Name field is required."),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter API key description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <DrawerFooter sticky>
              <DrawerFooterActions>
                <Button variant="secondary" onPress={handleCloseDrawer}>
                  Cancel
                </Button>
                <Button variant="primary" onPress={handleSubmitClick} loading={isSubmitting}>
                  Save
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showApiKeysEditDrawer = createDrawerTrigger(ApiKeysEditDrawer);
